import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-screen w-full bg-brand-bg overflow-hidden flex items-center" data-id="element-160">
      {/* Animated Background */}
      <div className="absolute inset-0" data-id="element-161">
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-grid opacity-20" data-id="element-162" />

        {/* Topographic Lines */}
        <div className="absolute inset-0 bg-topo opacity-10" data-id="element-163" />

        {/* Floating Coordinates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-164">
          <span className="absolute top-[15%] left-[10%] font-mono text-xs text-brand-accent/20" data-id="element-165">
            19.4326° N
          </span>
          <span className="absolute top-[25%] right-[15%] font-mono text-xs text-brand-accent/20" data-id="element-166">
            -99.1332° W
          </span>
          <span className="absolute bottom-[30%] left-[20%] font-mono text-xs text-brand-accent/20" data-id="element-167">
            25.6866° N
          </span>
          <span className="absolute bottom-[20%] right-[25%] font-mono text-xs text-brand-accent/20" data-id="element-168">
            -100.3161° W
          </span>
          <span className="absolute top-[40%] left-[5%] font-mono text-xs text-brand-accent/15" data-id="element-169">
            20.9674° N
          </span>
          <span className="absolute top-[60%] right-[8%] font-mono text-xs text-brand-accent/15" data-id="element-170">
            -89.5926° W
          </span>
        </div>

        {/* Animated Route Lines */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-171">
          <defs data-id="element-172">
            <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-173">
              <stop offset="0%" stopColor="#00c8ff" stopOpacity="0" data-id="element-174" />
              <stop offset="50%" stopColor="#00c8ff" stopOpacity="0.4" data-id="element-175" />
              <stop offset="100%" stopColor="#00c8ff" stopOpacity="0" data-id="element-176" />
            </linearGradient>
          </defs>

          {/* Route Path 1 */}
          <path d="M-50,400 Q200,350 400,450 T800,350 T1200,400 T1600,300" fill="none" stroke="url(#routeGradient)" strokeWidth="2" strokeDasharray="10 5" className="animate-[dashMove_20s_linear_infinite]" data-id="element-177" />

          {/* Route Path 2 */}
          <path d="M-50,600 Q300,550 500,650 T900,500 T1300,600" fill="none" stroke="url(#routeGradient)" strokeWidth="1.5" strokeDasharray="8 4" className="animate-[dashMove_25s_linear_infinite]" style={{
          animationDelay: '-5s'
        }} data-id="element-178" />

          {/* Moving Vehicle Dot */}
          <circle r="6" fill="#00c8ff" className="animate-[pulseGlow_2s_ease-in-out_infinite]" data-id="element-179">
            <animateMotion dur="15s" repeatCount="indefinite" path="M-50,400 Q200,350 400,450 T800,350 T1200,400 T1600,300" data-id="element-180" />
          </circle>
          <circle r="3" fill="#ffffff" data-id="element-181">
            <animateMotion dur="15s" repeatCount="indefinite" path="M-50,400 Q200,350 400,450 T800,350 T1200,400 T1600,300" data-id="element-182" />
          </circle>
        </svg>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-bg via-brand-bg/80 to-transparent" data-id="element-183" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-transparent to-brand-bg/50" data-id="element-184" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32" data-id="element-185">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-186">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-187">
            {/* Eyebrow */}
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2
          }} className="font-mono text-xs tracking-[0.2em] text-brand-accent mb-6" data-id="element-188">
              RASTREO SATELITAL PROFESIONAL | MÉXICO
            </motion.p>

            {/* Headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }} className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.1] mb-6" data-id="element-189">
              Controla tu flotilla.
              <br data-id="element-190" />
              <span className="text-brand-accent" data-id="element-191">Protege tus activos.</span>
              <br data-id="element-192" />
              En tiempo real.
            </motion.h1>

            {/* Subtext */}
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="font-body text-lg text-brand-text-secondary max-w-xl mb-8" data-id="element-193">
              Soluciones GPS empresariales y personales con monitoreo 24/7, app
              móvil y plataforma web avanzada.
            </motion.p>

            {/* CTAs */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-194">
              <a href="#contacto" className="inline-flex items-center justify-center bg-brand-accent text-brand-bg font-subheading font-semibold px-8 py-4 rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:shadow-xl hover:shadow-brand-accent/25 hover:scale-[1.02]" data-id="element-195">
                Solicitar Cotización
              </a>
              <a href="#soluciones" className="inline-flex items-center justify-center border-2 border-brand-accent text-brand-accent font-subheading font-semibold px-8 py-4 rounded-lg hover:bg-brand-accent/10 transition-all duration-200" data-id="element-196">
                Ver Soluciones
              </a>
            </motion.div>

            {/* Trust Badges */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.8
          }} className="flex flex-wrap gap-6 text-sm" data-id="element-197">
              <div className="flex items-center gap-2 text-brand-text-secondary" data-id="element-198">
                <MapPinIcon className="w-4 h-4 text-brand-accent" data-id="element-199" />
                <span className="font-body" data-id="element-200">Instalación a Domicilio</span>
              </div>
              <div className="flex items-center gap-2 text-brand-text-secondary" data-id="element-201">
                <ClockIcon className="w-4 h-4 text-brand-accent" data-id="element-202" />
                <span className="font-body" data-id="element-203">Soporte 24/7</span>
              </div>
              <div className="flex items-center gap-2 text-brand-text-secondary" data-id="element-204">
                <ShieldCheckIcon className="w-4 h-4 text-brand-accent" data-id="element-205" />
                <span className="font-body" data-id="element-206">Sin contratos forzosos</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Dashboard Mockup */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4,
          duration: 0.8
        }} className="relative" data-id="element-207">
            <div className="glass-strong rounded-2xl p-4 shadow-2xl shadow-brand-accent/10" data-id="element-208">
              {/* Dashboard Header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-cyan-subtle" data-id="element-209">
                <div className="flex items-center gap-3" data-id="element-210">
                  <div className="w-8 h-8 rounded-lg bg-brand-accent/20 flex items-center justify-center" data-id="element-211">
                    <MapPinIcon className="w-4 h-4 text-brand-accent" data-id="element-212" />
                  </div>
                  <span className="font-subheading font-medium text-white" data-id="element-213">
                    TrackerSpace Dashboard
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30" data-id="element-214">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-blink" data-id="element-215" />
                  <span className="font-mono text-xs text-red-400" data-id="element-216">LIVE</span>
                </div>
              </div>

              {/* Dashboard Content */}
              <div className="grid grid-cols-3 gap-4" data-id="element-217">
                {/* Map Area */}
                <div className="col-span-2 bg-brand-bg rounded-xl p-3 min-h-[280px] relative overflow-hidden" data-id="element-218">
                  {/* Mini Map Grid */}
                  <div className="absolute inset-0 bg-grid opacity-30" data-id="element-219" />

                  {/* Map Routes */}
                  <svg className="absolute inset-0 w-full h-full" data-id="element-220">
                    <path d="M40,180 Q80,120 140,140 T220,100" fill="none" stroke="#00c8ff" strokeWidth="2" strokeOpacity="0.5" data-id="element-221" />
                    <path d="M60,220 Q120,200 180,240" fill="none" stroke="#00c8ff" strokeWidth="1.5" strokeOpacity="0.3" data-id="element-222" />
                  </svg>

                  {/* Vehicle Markers */}
                  <div className="absolute top-[30%] left-[25%]" data-id="element-223">
                    <div className="relative" data-id="element-224">
                      <div className="w-4 h-4 rounded-full bg-brand-accent animate-ping absolute" data-id="element-225" />
                      <div className="w-4 h-4 rounded-full bg-brand-accent relative z-10 flex items-center justify-center" data-id="element-226">
                        <div className="w-2 h-2 rounded-full bg-white" data-id="element-227" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-[50%] left-[55%]" data-id="element-228">
                    <div className="w-3 h-3 rounded-full bg-brand-accent/70 flex items-center justify-center" data-id="element-229">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-230" />
                    </div>
                  </div>
                  <div className="absolute top-[65%] left-[35%]" data-id="element-231">
                    <div className="w-3 h-3 rounded-full bg-brand-text-secondary flex items-center justify-center" data-id="element-232">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-233" />
                    </div>
                  </div>
                  <div className="absolute top-[40%] left-[70%]" data-id="element-234">
                    <div className="w-3 h-3 rounded-full bg-brand-accent/70 flex items-center justify-center" data-id="element-235">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-236" />
                    </div>
                  </div>

                  {/* Coordinates */}
                  <div className="absolute bottom-2 left-2 font-mono text-[10px] text-brand-accent/60" data-id="element-237">
                    19.4326° N, -99.1332° W
                  </div>
                </div>

                {/* Vehicle List Sidebar */}
                <div className="space-y-3" data-id="element-238">
                  <VehicleCard name="Unidad 01" vehicle="Ford Transit" status="moving" speed={67} data-id="element-239" />
                  <VehicleCard name="Unidad 02" vehicle="Nissan NP300" status="parked" speed={0} data-id="element-240" />
                  <VehicleCard name="Unidad 03" vehicle="Chevrolet Aveo" status="moving" speed={42} data-id="element-241" />
                  <VehicleCard name="Unidad 04" vehicle="Toyota Hilux" status="parked" speed={0} data-id="element-242" />
                </div>
              </div>

              {/* Dashboard Footer Stats */}
              <div className="grid grid-cols-4 gap-3 mt-4 pt-3 border-t border-cyan-subtle" data-id="element-243">
                <StatMini label="Activos" value="4" data-id="element-244" />
                <StatMini label="En Movimiento" value="2" data-id="element-245" />
                <StatMini label="Alertas" value="0" data-id="element-246" />
                <StatMini label="Km Hoy" value="234" data-id="element-247" />
              </div>
            </div>

            {/* Decorative Glow */}
            <div className="absolute -inset-4 bg-brand-accent/5 rounded-3xl blur-3xl -z-10" data-id="element-248" />
          </motion.div>
        </div>
      </div>
    </section>;
}
function VehicleCard({
  name,
  vehicle,
  status,
  speed
}: {
  name: string;
  vehicle: string;
  status: 'moving' | 'parked';
  speed: number;
}) {
  return <div className="bg-brand-surface/50 rounded-lg p-2.5 border border-cyan-subtle" data-id="element-249">
      <div className="flex items-center gap-2 mb-1" data-id="element-250">
        <div className={`w-2 h-2 rounded-full ${status === 'moving' ? 'bg-brand-accent' : 'bg-brand-text-secondary'}`} data-id="element-251" />
        <span className="font-subheading text-xs text-white truncate" data-id="element-252">
          {name}
        </span>
      </div>
      <p className="font-body text-[10px] text-brand-text-secondary truncate mb-1" data-id="element-253">
        {vehicle}
      </p>
      <div className="flex items-center justify-between" data-id="element-254">
        <span className="font-mono text-[10px] text-brand-accent" data-id="element-255">
          {speed} km/h
        </span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded ${status === 'moving' ? 'bg-brand-accent/20 text-brand-accent' : 'bg-brand-text-secondary/20 text-brand-text-secondary'}`} data-id="element-256">
          {status === 'moving' ? 'En movimiento' : 'Estacionado'}
        </span>
      </div>
    </div>;
}
function StatMini({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="text-center" data-id="element-257">
      <p className="font-heading text-lg text-white" data-id="element-258">{value}</p>
      <p className="font-body text-[10px] text-brand-text-secondary" data-id="element-259">{label}</p>
    </div>;
}